/**
 * Proposte: chiunque propone (classificazione, collegamento claim-red flag, nuova voce di lista), l'admin approva, modifica
 * o rifiuta, anche più proposte insieme. Chi non è admin vede le proprie proposte e il loro esito.
 */
import { App, Button, Checkbox, Input, Modal, Select, Space, Table, Tabs } from "antd";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  ANCORA,
  LISTE,
  TIPI_PROPOSTA,
  type ContenutoCollegamentoT,
  type ContenutoNuovaVoceT,
  type EsitoDecisioniDto,
  type Lista,
  type PaginaDto,
  type PropostaRigaDto,
  type TipoProposta,
} from "@qa/shared";
import { api } from "../api/client";
import { fmtDataOra } from "../lib/format";
import { usePuo } from "../lib/permessi";
import { useAggiorna } from "../lib/query";
import { messaggioErrore } from "../i18n/errori";
import { PageHead, StatoPropostaTag, TipoPropostaTag } from "../components/ui";
import { Coppia, ElencoSchede } from "../components/Elenco";
import { useStretto } from "../lib/schermo";

type Tab = "da_approvare" | "approvate" | "rifiutate";

function useEsito() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  return (e: EsitoDecisioniDto) => {
    if (e.approvate.length)
      message.success([t("proposte.approvate", { n: e.approvate.length }), e.ncAperte.length ? t("proposte.ncAperte", { codici: e.ncAperte.join(", ") }) : ""].filter(Boolean).join(" · "));
    if (e.errori.length) message.error(t("proposte.errori", { n: e.errori.length, dettaglio: e.errori.map((x) => `${x.codice}: ${x.messaggio}`).join("; ") }), 8);
  };
}

function ModificaProposta({ p, onClose }: { p: PropostaRigaDto; onClose: () => void }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const aggiorna = useAggiorna();
  const esito = useEsito();
  const [coll, setColl] = useState<ContenutoCollegamentoT | null>(p.tipo === "collegamento_claim" ? (p.contenuto as ContenutoCollegamentoT) : null);
  const [voce, setVoce] = useState<ContenutoNuovaVoceT | null>(p.tipo === "nuova_voce" ? (p.contenuto as ContenutoNuovaVoceT) : null);
  const [nota, setNota] = useState("");
  const m = useMutation({
    mutationFn: () => api.post<EsitoDecisioniDto>(`/proposte/${p.id}/modifica`, { contenuto: coll ?? voce, nota: nota.trim() || null }),
    onSuccess: (e) => {
      esito(e);
      void aggiorna();
      onClose();
    },
    onError: (e) => message.error(messaggioErrore(e)),
  });
  return (
    <Modal
      open
      onCancel={onClose}
      title={t("proposte.modificaTitolo", { codice: p.codice })}
      okText={t("redflag.scheda.modificaApprova")}
      cancelText={t("azioni.annulla")}
      confirmLoading={m.isPending}
      onOk={() => m.mutate()}
    >
      <Space direction="vertical" style={{ width: "100%" }}>
        <div className="muted">{p.descrizione}</div>
        {coll && (
          <Checkbox checked={coll.daVerificare} onChange={(e) => setColl({ ...coll, daVerificare: e.target.checked })}>
            {t("claim.scheda.daVerificare")}
          </Checkbox>
        )}
        {voce && (
          <>
            <div className="muted small">{t("proposte.lista")}</div>
            <Select<Lista> value={voce.lista} onChange={(l) => setVoce({ ...voce, lista: l })} style={{ width: "100%" }} options={LISTE.map((l) => ({ value: l, label: t(`dominio.lista.${l}`) }))} />
            <div className="muted small">{t("proposte.valore")}</div>
            <Input value={voce.valore} maxLength={100} onChange={(e) => setVoce({ ...voce, valore: e.target.value })} />
          </>
        )}
        <Input.TextArea rows={2} maxLength={1000} placeholder={t("proposte.notaDecisione")} value={nota} onChange={(e) => setNota(e.target.value)} />
      </Space>
    </Modal>
  );
}

export default function PropostePage() {
  const { t } = useTranslation();
  const { message, modal } = App.useApp();
  const puo = usePuo();
  const nav = useNavigate();
  const aggiorna = useAggiorna();
  const esito = useEsito();
  const admin = puo("proposta.decidi");
  const stretto = useStretto();
  const [tab, setTab] = useState<Tab>("da_approvare");
  const [tipo, setTipo] = useState<TipoProposta | undefined>();
  const [da, setDa] = useState<"import" | "persone" | undefined>();
  const [sel, setSel] = useState<string[]>([]);
  const [modifica, setModifica] = useState<PropostaRigaDto | null>(null);
  const stato = tab === "da_approvare" ? "da_approvare" : tab === "rifiutate" ? "rifiutata" : undefined;
  const q = useQuery({
    queryKey: ["proposte", tab, tipo, da],
    queryFn: () => api.get<PaginaDto<PropostaRigaDto>>("/proposte", { stato, tipo, da, limit: 500 }),
    placeholderData: keepPreviousData,
  });
  const righe = (q.data?.righe ?? []).filter((p) => (tab === "approvate" ? p.stato === "approvata" || p.stato === "modificata" : true));

  const decidi = useMutation({
    mutationFn: async (a: { az: "approva" | "rifiuta" | "blocco"; id?: string; nota?: string }) => {
      if (a.az === "blocco") return api.post<EsitoDecisioniDto>("/proposte/approva", { ids: sel });
      if (a.az === "approva") return api.post<EsitoDecisioniDto>(`/proposte/${a.id}/approva`, {});
      return api.post<PropostaRigaDto>(`/proposte/${a.id}/rifiuta`, { nota: a.nota?.trim() || null });
    },
    onSuccess: (res, a) => {
      if (a.az === "rifiuta") message.success(t("redflag.scheda.rifiutataOk"));
      else esito(res as EsitoDecisioniDto);
      setSel([]);
      void aggiorna();
    },
    onError: (e) => message.error(messaggioErrore(e)),
  });
  const rifiuta = (p: PropostaRigaDto) => {
    let nota = "";
    modal.confirm({
      title: t("proposte.rifiutaTitolo", { codice: p.codice }),
      icon: null,
      okText: t("proposte.rifiuta"),
      okButtonProps: { danger: true },
      cancelText: t("azioni.annulla"),
      content: <Input.TextArea rows={2} maxLength={1000} placeholder={t("proposte.notaDecisione")} onChange={(e) => (nota = e.target.value)} />,
      onOk: () => decidi.mutateAsync({ az: "rifiuta", id: p.id, nota }),
    });
  };
  const oggetto = (p: PropostaRigaDto) =>
    p.segnalazioneId && p.tipo === "classificazione" ? (
      <Link className="mono" to={`/redflag/segnalazioni/${p.segnalazioneId}`}>
        {p.oggetto}
      </Link>
    ) : p.claimId ? (
      <Link className="mono" to={`/claim/${p.claimId}`}>
        {p.oggetto}
      </Link>
    ) : (
      <span>{p.oggetto}</span>
    );

  const azioni = (p: PropostaRigaDto) =>
    p.stato !== "da_approvare" ? (
      <>
        <StatoPropostaTag s={p.stato} />
        {p.decisaDa && <div className="muted small">{t("proposte.decisaDa", { chi: p.decisaDa, quando: fmtDataOra(p.decisaIl) })}</div>}
        {p.notaDecisione && <div className="muted small">{p.notaDecisione}</div>}
      </>
    ) : admin ? (
      <Space size={4} wrap>
        <Button size="small" type="primary" loading={decidi.isPending && decidi.variables?.id === p.id} onClick={() => decidi.mutate({ az: "approva", id: p.id })}>
          {t("proposte.approva")}
        </Button>
        <Button size="small" onClick={() => (p.tipo === "classificazione" && p.segnalazioneId ? nav(`/redflag/segnalazioni/${p.segnalazioneId}`) : setModifica(p))}>
          {t("proposte.modifica")}
        </Button>
        <Button size="small" danger onClick={() => rifiuta(p)}>
          {t("proposte.rifiuta")}
        </Button>
      </Space>
    ) : (
      <StatoPropostaTag s={p.stato} />
    );
  const selezionabile = admin && tab === "da_approvare";

  return (
    <>
      <PageHead
        crumbs={[{ label: t("portale.nome"), to: "/" }, { label: t("menu.gruppoProposte") }]}
        title={admin ? t("proposte.titoloAdmin") : t("proposte.titoloMie")}
        sub={t("proposte.sottotitolo")}
        extra={
          admin &&
          tab === "da_approvare" && (
            <Button
              type="primary"
              disabled={!sel.length}
              loading={decidi.isPending && decidi.variables?.az === "blocco"}
              onClick={() => decidi.mutate({ az: "blocco" })}
              data-tip={ANCORA.proposteApprova}
            >
              {t("proposte.approvaSelezionate", { n: sel.length })}
            </Button>
          )
        }
      />
      <Tabs
        activeKey={tab}
        onChange={(k) => {
          setTab(k as Tab);
          setSel([]);
        }}
        items={[
          { key: "da_approvare", label: `${t("proposte.tabDaApprovare")}${tab === "da_approvare" && q.data ? ` (${q.data.totale})` : ""}` },
          { key: "approvate", label: t("proposte.tabApprovate") },
          { key: "rifiutate", label: t("proposte.tabRifiutate") },
        ]}
      />
      <Space wrap style={{ marginBottom: 14 }}>
        <Select
          allowClear
          placeholder={t("proposte.tuttiTipi")}
          value={tipo}
          onChange={setTipo}
          style={{ width: stretto ? "calc(50% - 4px)" : 200 }}
          options={TIPI_PROPOSTA.map((x) => ({ value: x, label: t(`dominio.tipoProposta.${x}`) }))}
        />
        <Select
          allowClear
          placeholder={t("proposte.daChiunque")}
          value={da}
          onChange={setDa}
          style={{ width: stretto ? "calc(50% - 4px)" : 200 }}
          options={[
            { value: "import", label: t("proposte.daImport") },
            { value: "persone", label: t("proposte.daPersone") },
          ]}
        />
      </Space>
      {stretto ? (
        <ElencoSchede
          righe={righe}
          chiave={(p) => p.id}
          loading={q.isFetching}
          vuoto={q.isError ? messaggioErrore(q.error) : t("proposte.vuoto")}
          scheda={(p) => (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                <span>
                  {selezionabile && <Checkbox checked={sel.includes(p.id)} onChange={(e) => setSel(e.target.checked ? [...sel, p.id] : sel.filter((x) => x !== p.id))} style={{ marginRight: 8 }} />}
                  <span className="mono">{p.codice}</span>
                </span>
                <TipoPropostaTag tipo={p.tipo} />
              </div>
              <div style={{ marginTop: 4 }}>{oggetto(p)}</div>
              <div className="small">{p.descrizione}</div>
              {p.nota && <div className="muted small">{p.nota}</div>}
              <Coppia l={t("proposte.da")}>{p.propostaDa}</Coppia>
              <Coppia l={t("proposte.quando")}>
                <span className="num">{fmtDataOra(p.propostaIl)}</span>
              </Coppia>
              <div style={{ marginTop: 8 }}>{azioni(p)}</div>
            </>
          )}
        />
      ) : (
        <Table<PropostaRigaDto>
          rowKey="id"
          size="middle"
          loading={q.isFetching}
          dataSource={righe}
          scroll={{ x: 1100 }}
          pagination={{ pageSize: 50, hideOnSinglePage: true, showSizeChanger: false }}
          locale={{ emptyText: q.isError ? messaggioErrore(q.error) : t("proposte.vuoto") }}
          rowSelection={selezionabile ? { selectedRowKeys: sel, onChange: (k) => setSel(k as string[]) } : undefined}
          columns={[
            { title: t("proposte.proposta"), key: "c", width: 90, render: (_, p) => <span className="mono">{p.codice}</span> },
            { title: t("proposte.tipo"), key: "t", width: 170, render: (_, p) => <TipoPropostaTag tipo={p.tipo} /> },
            { title: t("proposte.oggetto"), key: "o", width: 160, render: (_, p) => oggetto(p) },
            {
              title: t("proposte.cosa"),
              key: "d",
              render: (_, p) => (
                <>
                  {p.descrizione}
                  {p.nota && <div className="muted small">{p.nota}</div>}
                </>
              ),
            },
            { title: t("proposte.da"), dataIndex: "propostaDa", width: 150 },
            { title: t("proposte.quando"), key: "q", width: 140, render: (_, p) => <span className="num">{fmtDataOra(p.propostaIl)}</span> },
            {
              title: tab === "da_approvare" ? "" : t("proposte.esito"),
              key: "a",
              width: tab === "da_approvare" && admin ? 250 : 200,
              render: (_, p) => azioni(p),
            },
          ]}
        />
      )}
      {modifica && <ModificaProposta p={modifica} onClose={() => setModifica(null)} />}
    </>
  );
}
